import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ArrowLeft, Check, Stethoscope, Wrench } from "lucide-react";
import { toast } from "sonner";

/**
 * Branching diagnostic — walks a 6-branch decision tree (27 outcomes).
 * Preceded by an optional context step: spa brand, model, free description.
 */
export default function Diagnostic() {
  const navigate = useNavigate();
  const [tree, setTree] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Context (Phase 2)
  const [phase, setPhase] = useState("context"); // 'context' | 'walk'
  const [spaBrand, setSpaBrand] = useState("");
  const [spaModel, setSpaModel] = useState("");
  const [problemDescription, setProblemDescription] = useState("");

  // Walk state
  const [currentQid, setCurrentQid] = useState(null);
  const [history, setHistory] = useState([]);
  const [answers, setAnswers] = useState({});
  const [path, setPath] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/diagnostic/tree")
      .then(({ data }) => {
        setTree(data);
        setBrands(data.brands || []);
        setCurrentQid(data.start_question_id);
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const currentQuestion = tree && currentQid ? tree.questions[currentQid] : null;

  const progress = useMemo(() => {
    if (phase === "context") return 5;
    if (!currentQuestion) return 0;
    return Math.min(90, ((history.length + 1) / 3) * 100);
  }, [phase, history.length, currentQuestion]);

  const pickAnswer = (answer) => {
    setSelected(answer);
    setAnswers((prev) => ({ ...prev, [currentQid]: answer.value }));
  };

  const startWalk = () => setPhase("walk");

  const continueWalk = async () => {
    if (!selected) {
      toast.error("Merci de choisir une réponse.");
      return;
    }
    const step = {
      question_id: currentQid,
      answer_value: selected.value,
      answer_label: selected.label,
    };
    const newPath = [...path, step];

    if (selected.result) {
      setSubmitting(true);
      try {
        const { data } = await api.post("/diagnostic/submit", {
          result_id: selected.result,
          answers: { ...answers, [currentQid]: selected.value },
          path: newPath,
          spa_brand: spaBrand || undefined,
          spa_model: spaModel.trim() || undefined,
          problem_description: problemDescription.trim() || undefined,
        });
        toast.success("Diagnostic terminé");
        navigate(`/results/${data.id}`, { replace: true });
      } catch (err) {
        if (err?.response?.status === 401) {
          toast.error("Veuillez vous connecter pour lancer un diagnostic.");
          navigate("/login", { state: { from: { pathname: "/diagnostic" } } });
        } else {
          toast.error(formatApiError(err));
        }
        setSubmitting(false);
      }
      return;
    }

    if (selected.next) {
      setHistory((prev) => [...prev, currentQid]);
      setPath(newPath);
      setCurrentQid(selected.next);
      setSelected(null);
    }
  };

  const goBack = () => {
    if (history.length === 0) {
      setPhase("context");
      setSelected(null);
      return;
    }
    const previous = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setPath((prev) => prev.slice(0, -1));
    setCurrentQid(previous);
    const prevAnswerValue = answers[previous];
    const prevQ = tree.questions[previous];
    const prevAnswer =
      prevQ?.answers.find((a) => a.value === prevAnswerValue) ?? null;
    setSelected(prevAnswer);
  };

  const restart = () => {
    if (!tree) return;
    setPhase("context");
    setCurrentQid(tree.start_question_id);
    setHistory([]);
    setPath([]);
    setAnswers({});
    setSelected(null);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-10 py-12" data-testid="diagnostic-page">
      <div className="flex items-center justify-between mb-4">
        <div className="eyebrow">Diagnostic technique</div>
        <div className="text-xs text-muted-foreground">
          {phase === "context"
            ? "Contexte"
            : <>Question <span className="font-semibold text-foreground">{history.length + 1}</span></>}
        </div>
      </div>
      <Progress value={progress} className="h-1.5" data-testid="diagnostic-progress" />

      {phase === "context" ? (
        <Card className="mt-8 p-8 lg:p-10 border-black/10 shadow-sm" data-testid="context-step">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-11 w-11 rounded-lg bg-primary/10 text-primary">
              <Wrench className="h-5 w-5" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary">
                Étape 1 · Votre spa
              </p>
              <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight mt-0.5">
                Parlez-nous de votre équipement
              </h1>
            </div>
          </div>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
            Ces informations sont facultatives mais améliorent la précision du diagnostic.
          </p>

          <div className="mt-8 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="spa-brand">Marque du spa</Label>
                <select
                  id="spa-brand"
                  value={spaBrand}
                  onChange={(e) => setSpaBrand(e.target.value)}
                  data-testid="spa-brand-select"
                  className="h-10 w-full rounded-md border border-black/10 bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="">Sélectionner…</option>
                  {brands.map((b) => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="spa-model">Modèle</Label>
                <Input
                  id="spa-model"
                  value={spaModel}
                  onChange={(e) => setSpaModel(e.target.value)}
                  data-testid="spa-model-input"
                  placeholder="Ex : J-335, Prima 3P…"
                  maxLength={120}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="problem-description">Décrivez votre problème (facultatif)</Label>
              <Textarea
                id="problem-description"
                value={problemDescription}
                onChange={(e) => setProblemDescription(e.target.value)}
                data-testid="problem-description-input"
                placeholder="Ex : ce matin le spa affiche FLO après une vidange, la pompe tourne mais l'eau ne chauffe plus…"
                rows={5}
                maxLength={2000}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground text-right">
                {problemDescription.length} / 2000
              </p>
            </div>
          </div>

          <div className="mt-10 flex items-center justify-end pt-8 border-t border-black/5">
            <Button
              onClick={startWalk}
              className="rounded-full px-6"
              data-testid="context-continue"
            >
              Commencer le diagnostic <Check className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </Card>
      ) : currentQuestion ? (
        <Card className="mt-8 p-8 lg:p-10 border-black/10 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-11 w-11 rounded-lg bg-primary/10 text-primary">
              <Stethoscope className="h-5 w-5" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary">
                Étape 2 · Diagnostic
              </p>
              <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight mt-0.5" data-testid="question-title">
                {currentQuestion.title}
              </h1>
            </div>
          </div>

          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
            {currentQuestion.description}
          </p>

          <div className="mt-8 grid gap-3">
            {currentQuestion.answers.map((answer) => {
              const isActive = selected?.value === answer.value;
              return (
                <button
                  key={answer.value}
                  type="button"
                  onClick={() => pickAnswer(answer)}
                  data-testid={`answer-${currentQuestion.id}-${answer.value}`}
                  className={`text-left rounded-xl border px-5 py-4 font-medium transition-colors ${
                    isActive
                      ? "border-primary bg-primary/5 text-foreground"
                      : "border-black/10 bg-background hover:border-primary/40"
                  }`}
                >
                  {answer.label}
                </button>
              );
            })}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-3 pt-8 border-t border-black/5">
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={goBack}
                disabled={submitting}
                data-testid="diagnostic-prev"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Précédent
              </Button>
              {history.length > 0 && (
                <Button
                  variant="ghost"
                  onClick={restart}
                  disabled={submitting}
                  data-testid="diagnostic-restart"
                  className="text-muted-foreground"
                >
                  Recommencer
                </Button>
              )}
            </div>
            <Button
              onClick={continueWalk}
              disabled={!selected || submitting}
              className="rounded-full px-6"
              data-testid="diagnostic-next"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : selected?.result ? (
                <>Voir le diagnostic <Check className="h-4 w-4 ml-2" /></>
              ) : (
                <>Continuer <Check className="h-4 w-4 ml-2" /></>
              )}
            </Button>
          </div>
        </Card>
      ) : null}

      <p className="mt-6 text-xs text-muted-foreground text-center">
        Coupez toujours l'alimentation électrique avant toute intervention.
      </p>
    </div>
  );
}
